import config from '../config/environment';
import supabase from '../config/supabase';
import type {
  DashboardDelinquentRow,
  DashboardDuePaymentsSnapshot,
  DashboardOperationalSnapshot,
  DashboardSupportSnapshot,
} from '../types/dashboard';
import type { PaginatedResponse } from '../types';
import { mapDashboardDuePayments } from '../utils/dashboardDuePayments';
import { mapDashboardOperationalSnapshot } from '../utils/dashboardSnapshot';
import { mapDashboardDelinquentPage, mapDashboardSupportSnapshot } from '../utils/dashboardSupportSnapshot';
import { handleServiceError } from '../utils/errorHandler';

class DashboardServiceClass {
  async getOperationalSnapshot(competence?: string): Promise<DashboardOperationalSnapshot> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const url = new URL(`${config.supabaseFunctionsUrl}/dashboard-snapshot`);
    if (competence) {
      url.searchParams.set('competence', competence);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error((err as { error?: string })?.error || 'Erro ao carregar dashboard'),
        'carregar dashboard',
      );
    }

    const body = await response.json() as { data?: unknown };
    return mapDashboardOperationalSnapshot(body.data);
  }

  async getSupportSnapshot(competence?: string): Promise<DashboardSupportSnapshot> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const url = new URL(`${config.supabaseFunctionsUrl}/dashboard-snapshot`);
    url.searchParams.set('view', 'support');
    if (competence) {
      url.searchParams.set('competence', competence);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error((err as { error?: string })?.error || 'Erro ao carregar dashboard de suporte'),
        'carregar dashboard de suporte',
      );
    }

    const body = await response.json() as { data?: unknown };
    return mapDashboardSupportSnapshot(body.data);
  }

  async listDelinquents(query: {
    page: number;
    pageSize: number;
    searchName: string;
    paymentGateway: readonly string[];
    paidStart: string;
    paidEnd: string;
  }): Promise<PaginatedResponse<DashboardDelinquentRow>> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const url = new URL(`${config.supabaseFunctionsUrl}/dashboard-snapshot`);
    url.searchParams.set('view', 'delinquents');
    url.searchParams.set('page', String(query.page));
    url.searchParams.set('pageSize', String(query.pageSize));

    const searchName = query.searchName.trim();
    if (searchName) {
      url.searchParams.set('searchName', searchName);
    }

    if (query.paymentGateway.length > 0) {
      url.searchParams.set('paymentGateway', query.paymentGateway.join(','));
    }

    const paidStart = query.paidStart.trim();
    const paidEnd = query.paidEnd.trim();
    if (paidStart && paidEnd) {
      url.searchParams.set('paidStart', paidStart);
      url.searchParams.set('paidEnd', paidEnd);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error((err as { error?: string })?.error || 'Erro ao carregar inadimplentes'),
        'carregar inadimplentes',
      );
    }

    const body = await response.json();
    const page = mapDashboardDelinquentPage(body);
    if (!page) {
      handleServiceError(
        new Error('Erro ao carregar inadimplentes'),
        'carregar inadimplentes',
      );
    }

    return page;
  }

  async updateDelinquencyFollowUp(input: {
    studentId: number;
    action: 'update-stage' | 'update-notes';
    stage?: string;
    notes?: string;
  }): Promise<{ stage: string | null; notes: string }> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(`${config.supabaseFunctionsUrl}/delinquency-follow-up`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error((err as { error?: string })?.error || 'Erro ao salvar acompanhamento'),
        'salvar acompanhamento da inadimplente',
      );
    }

    const body = await response.json() as { stage?: unknown; notes?: unknown };
    return {
      stage: typeof body.stage === 'string' ? body.stage : null,
      notes: typeof body.notes === 'string' ? body.notes : '',
    };
  }

  async getDuePayments(dueStart = '', dueEnd = ''): Promise<DashboardDuePaymentsSnapshot> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const url = new URL(`${config.supabaseFunctionsUrl}/dashboard-snapshot`);
    url.searchParams.set('view', 'payments');
    if (dueStart && dueEnd) {
      url.searchParams.set('dueStart', dueStart);
      url.searchParams.set('dueEnd', dueEnd);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error((err as { error?: string })?.error || 'Erro ao carregar pagamentos do dashboard'),
        'carregar pagamentos do dashboard',
      );
    }

    const body = await response.json() as { data?: unknown };
    return mapDashboardDuePayments(body.data);
  }
}

export const DashboardService = new DashboardServiceClass();
